import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CODE_TYPE } from 'app/models/buckzy-constants/buckzy-constants.model';
import { BuckzyCode } from 'app/models/handle-party/buckzy-code.model';
import { WalletCurrency } from 'app/models/handle-party/wallet/currencies/wallet-currency.model';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Country } from 'app/models/users/country.model';
import { Role } from 'app/models/customers/entity.model';

const CURRENCY_URI = '/v1/currencies';
const CLIENTS_URI = '/v1/clients';
const COUNTRY_URI = '/v1/countries';
const PURPOSE_URI = '/v1/purpose-codes';
const AGENT_URI = '/v1/agents';
const BUCKZY_CODES_URI = '/v1/buckzy-codes';
const ROLE_URI = '/v1/associate-roles';
const OCCUPATION_CODES_URI = '/v1/occupations';

@Injectable({
  providedIn: 'root',
})
export class GeneralService {

  constructor(private http: HttpClient) { }

    getAllCurrencies(): Observable<HttpResponse<any>> {
      const h = {
        'Content-Type': 'application/json',
        'access-control-expose-headers': '*',
      };
      let URL = environment.BASE_URL + CURRENCY_URI;

      return this.http.get<any>(URL, { headers: h, observe: 'response' });
    }

  getSupportingCurrencies(country?: string): Observable<HttpResponse<any>> {
    let params = new HttpParams();
    if (country) {
      params = params.set('country', country);
    }

    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    let URL = environment.BASE_URL + CURRENCY_URI + '?support=sending';

    return this.http.get<any>(URL, { headers: h, observe: 'response', params: params });
  }

  getWalletCurrencies(support = 'sending'): Observable<WalletCurrency[]> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    const URL = `${environment.BASE_URL}${CURRENCY_URI}`;

    let params = new HttpParams();
    if (support) {
      params = params.set('support', support);
    }

    let currencyResponse = this.http.get<any[]>(URL, { headers: h, observe: 'response', params: params }).pipe(
      take(1),
      map(response => {
        return response.body
          .map(walletCurrency => {
            return new WalletCurrency(walletCurrency.currencyCode, walletCurrency.currencyName);
          })
          .filter((value, index, self) => {
            return index === self.findIndex((currency) => {
              return currency.currencyCode === value.currencyCode;
            });
          });
      })
    );

    return currencyResponse;
  }

  getPurpose(type): Observable<HttpResponse<any>> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    let URL = environment.BASE_URL + PURPOSE_URI + '?tp=' + type;

    return this.http.get<any>(URL, { headers: h, observe: 'response' });
  }

  getSupportingCountries(clientID?: string): Observable<HttpResponse<any>> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    let URL = environment.BASE_URL + COUNTRY_URI + '?support=true';

    if (clientID) {
      URL = URL + '&clientId=' + clientID
    }

    return this.http.get<any>(URL, { headers: h, observe: 'response' })
      .pipe(
        map(response => {
          return response.body.map(country => {
            if (country.countryCode != 'UK') {
              return new Country(
                country.callCode,
                country.countryCode,
                country.countryName,
                country.currencies,
                country.internationalDialPrefix);

            }
          }).filter(Boolean);
        })
      );
  }

  getSupportingReceivingCurrencies(countryCode?: string): Observable<HttpResponse<any>> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    let URL = environment.BASE_URL + CURRENCY_URI + '?support=receiving';
    if (countryCode) {
      URL += '&countryCode=' + countryCode;
    }

    return this.http.get<any>(URL, { headers: h, observe: 'response' });
  }

  getReceivingCurrenciesForRamp(sourceCurrencyCode: string): Observable<HttpResponse<any>> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    const URL = environment.BASE_URL + CURRENCY_URI + '?support=receiving-digital-asset&sourceCurrencyCode=' + sourceCurrencyCode;
    return this.http.get<any>(URL, { headers: h, observe: 'response' });
  }

  getNetworks(currencyCode: string): Observable<string[]> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    const URL = `${environment.BASE_URL}/v1/${currencyCode}/networks`;
    return this.http.get<string[]>(URL, { headers: h });
  }

  getProvinces(countryCode: string): Observable<HttpResponse<any>> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    let URL = environment.BASE_URL + COUNTRY_URI + '/' + countryCode + '/states';

    return this.http.get<any>(URL, { headers: h, observe: 'response' });
  }

  getCountries(): Observable<Country[]> {
    const headers = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };

    const URL = `${environment.BASE_URL}${COUNTRY_URI}`;
    const options = {
      headers: headers,
      observe: 'response' as const,
    }

    return this.http.get<Country[]>(URL, options).pipe(
      map(response => {
        return response.body.map(country => {
          if (country.countryCode != 'UK') {
            return new Country(
              country.callCode,
              country.countryCode,
              country.countryName,
              country.currencies,
              country.internationalDialPrefix);

          }
        }).filter(Boolean);
      })
    );
  }

  getRoles() {
    return this.http.get<Role[]>(`${environment.BASE_URL}/${ROLE_URI}`);
  }

  getClients(): Observable<HttpResponse<any>> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    const URL = environment.BASE_URL + CLIENTS_URI;
    return this.http.get<any>(URL, { headers: h, observe: 'response' });
  }

  getSupportingProvinces(country?: string): Observable<HttpResponse<any>> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    let URL = environment.BASE_URL + COUNTRY_URI + '/' + country + '/states';

    return this.http.get<any>(URL, { headers: h, observe: 'response' });
  }

  getSupportingCities(country?: string, province?: string): Observable<HttpResponse<any>> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    let URL = environment.BASE_URL + COUNTRY_URI + '/' + country + '/states/' + province + '/cities';

    return this.http.get<any>(URL, { headers: h, observe: 'response' });
  }

  getAgentInformation(fromCurrencyCode: string, toCurrencyCode: string, toCountryCode: string, disbursementMethod: string): Observable<HttpResponse<any>> {
    const h = {
      'Content-Type': 'application/json',
      'access-control-expose-headers': '*',
    };
    const URL = environment.BASE_URL + AGENT_URI + "?fromCurrencyCode=" + fromCurrencyCode + "&toCurrencyCode=" + toCurrencyCode + "&toCountryCode=" + toCountryCode + "&disbursementMethod=" + disbursementMethod;
    return this.http.get<any>(URL, { headers: h, observe: 'response' });
  }

  getBuckzyCodes(codeType: CODE_TYPE, documentType?: PartyType): Observable<BuckzyCode[]> {

    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders
      .set('Content-Type', 'application/json')
      .set('access-control-expose-headers', '*');

    let httpParams = new HttpParams();
    httpParams = httpParams.set('category', codeType);

    if (documentType) {
      httpParams = httpParams.set('type', documentType.toUpperCase());
    }

    const options = {
      headers: httpHeaders,
      params: httpParams,
      observe: 'response' as const
    }

    const URL = `${environment.BASE_URL}${BUCKZY_CODES_URI}`;

    return this.http.get<any[]>(URL, options).map(response => {
      return response.body.map(buckzyCode => {
        return new BuckzyCode(buckzyCode.code, buckzyCode.description);
      });
    });
  }
  getOccupationCodes(): Observable<BuckzyCode[]> {
    return this.http.get<BuckzyCode[]>(environment.BASE_URL + OCCUPATION_CODES_URI);
  }
}
export type PartyType = 'I' | 'B' | 'i' | 'b';
